import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaEye, FaClock, FaUserCheck, FaUserTimes, FaInbox } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { parseEducation, getAlmamater } from '../utils/education';
import { notifyRequestApproved, notifyRequestRejected } from '../lib/emailService';

const AdminRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('alumni_requests')
      .select('*')
      .order('created_at', { ascending: false });
    setRequests(data || []);
    setLoading(false);
  };

  const handleApprove = async (req) => {
    setProcessing(req.id);
    try {
      // Insert into alumni table
      const { error: insertError } = await supabase.from('alumni').insert([{
        name: req.name,
        email: req.email,
        education: req.education,
        job: req.job,
        institution: req.institution,
        research_interests: req.research_interests,
        tahun_pimnas: req.tahun_pimnas,
        linkedin: req.linkedin,
        google_scholar: req.google_scholar,
        photo_url: req.photo_url,
      }]);

      if (insertError) throw insertError;

      // Update request status
      const { error: updateError } = await supabase
        .from('alumni_requests')
        .update({ status: 'approved' })
        .eq('id', req.id);

      if (updateError) throw updateError;

      // Send email notification to requester (non-blocking)
      notifyRequestApproved(req);

      setRequests(prev => prev.map(r => r.id === req.id ? { ...r, status: 'approved' } : r));
    } catch (err) {
      alert('Gagal approve: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Yakin ingin menolak pengajuan ini?')) return;
    setProcessing(id);
    try {
      const { error } = await supabase
        .from('alumni_requests')
        .update({ status: 'rejected' })
        .eq('id', id);

      if (error) throw error;

      // Send email notification to requester (non-blocking)
      const rejectedReq = requests.find(r => r.id === id);
      if (rejectedReq) notifyRequestRejected(rejectedReq);

      setRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
    } catch (err) {
      alert('Gagal reject: ' + err.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pengajuan ini permanen?')) return;
    const { error } = await supabase.from('alumni_requests').delete().eq('id', id);
    if (!error) setRequests(prev => prev.filter(r => r.id !== id));
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);

  const statusBadge = (status) => {
    if (status === 'pending') return <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaClock className="text-[10px]" /> Menunggu</span>;
    if (status === 'approved') return <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaUserCheck className="text-[10px]" /> Disetujui</span>;
    return <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full"><FaUserTimes className="text-[10px]" /> Ditolak</span>;
  };

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="font-inter bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-800 to-primary-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FaInbox className="text-gold-400" /> Pengajuan Alumni
          </h1>
          <p className="text-gray-300">Kelola pengajuan dari calon alumni PIMNAS</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { key: 'all', label: 'Semua', count: counts.all, color: 'bg-gray-100 text-gray-700' },
            { key: 'pending', label: 'Menunggu', count: counts.pending, color: 'bg-amber-100 text-amber-700' },
            { key: 'approved', label: 'Disetujui', count: counts.approved, color: 'bg-green-100 text-green-700' },
            { key: 'rejected', label: 'Ditolak', count: counts.rejected, color: 'bg-red-100 text-red-700' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`p-4 rounded-xl text-left transition-all ${filter === f.key ? 'ring-2 ring-primary-500 shadow-md' : 'hover:shadow-md'} ${f.color}`}
            >
              <p className="text-2xl font-bold">{f.count}</p>
              <p className="text-sm font-medium">{f.label}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FaInbox className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">Tidak ada pengajuan</h3>
            <p className="text-gray-400">Belum ada pengajuan pada kategori ini</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(req => (
              <div key={req.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition">
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Avatar */}
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      {req.photo_url ? (
                        <img src={req.photo_url} alt={req.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100 shrink-0" />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-lg font-bold shrink-0">
                          {req.name?.charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{req.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{req.email}</p>
                        <div className="flex flex-wrap gap-2 mt-1.5">
                          {req.job && <span className="bg-primary-50 text-primary-700 text-xs font-medium px-2.5 py-0.5 rounded-full">{req.job}{req.institution ? ` · ${req.institution}` : ''}</span>}
                          {req.tahun_pimnas && <span className="bg-gold-500/10 text-gold-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">🏆 PIMNAS ke-{req.tahun_pimnas}</span>}
                          {getAlmamater(req.education) && <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-0.5 rounded-full">🎓 {getAlmamater(req.education)}</span>}
                          {statusBadge(req.status)}
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="text-sm text-gray-400 shrink-0">
                      {new Date(req.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setDetailModal(req)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 px-3 py-2 rounded-lg transition"
                      >
                        <FaEye /> Detail
                      </button>
                      {req.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(req)}
                            disabled={processing === req.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 px-3 py-2 rounded-lg transition disabled:opacity-50"
                          >
                            <FaCheck /> Setujui
                          </button>
                          <button
                            onClick={() => handleReject(req.id)}
                            disabled={processing === req.id}
                            className="inline-flex items-center gap-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition disabled:opacity-50"
                          >
                            <FaTimes /> Tolak
                          </button>
                        </>
                      )}
                      {req.status !== 'pending' && (
                        <button
                          onClick={() => handleDelete(req.id)}
                          className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition"
                        >
                          <FaTimes /> Hapus
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Message preview */}
                  {req.message && (
                    <div className="mt-3 ml-[4.5rem] text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2 italic">
                      "{req.message}"
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setDetailModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="h-2 bg-gradient-to-r from-primary-500 to-gold-500"></div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                {detailModal.photo_url ? (
                  <img src={detailModal.photo_url} alt={detailModal.name} className="w-20 h-20 rounded-full object-cover ring-4 ring-primary-100" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-2xl font-bold">
                    {detailModal.name?.charAt(0)}
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{detailModal.name}</h2>
                  <p className="text-gray-500">{detailModal.email}</p>
                  <div className="mt-1">{statusBadge(detailModal.status)}</div>
                </div>
              </div>

              <div className="space-y-4">
                {detailModal.tahun_pimnas && (
                  <div className="bg-gold-500/5 border border-gold-500/20 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Partisipasi PIMNAS</h4>
                    <p className="text-sm font-semibold text-gold-600">🏆 PIMNAS ke-{detailModal.tahun_pimnas}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Pendidikan</h4>
                  {(() => {
                    const edu = parseEducation(detailModal.education);
                    const line = (k, j) => [k, j].filter(Boolean).join(' — ') || '';
                    return (
                      <div className="space-y-1">
                        {(edu.s1_kampus || edu.s1_jurusan) && <p className="text-sm text-gray-800"><span className="font-medium">S1:</span> {line(edu.s1_kampus, edu.s1_jurusan)}</p>}
                        {(edu.s2_kampus || edu.s2_jurusan) && <p className="text-sm text-gray-800"><span className="font-medium">S2:</span> {line(edu.s2_kampus, edu.s2_jurusan)}</p>}
                        {(edu.s3_kampus || edu.s3_jurusan) && <p className="text-sm text-gray-800"><span className="font-medium">S3:</span> {line(edu.s3_kampus, edu.s3_jurusan)}</p>}
                        {!edu.s1_kampus && !edu.s1_jurusan && !edu.s2_kampus && !edu.s3_kampus && <p className="text-sm text-gray-400">Belum diisi</p>}
                      </div>
                    );
                  })()}
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Karir</h4>
                  <p className="text-sm text-gray-800">{detailModal.job || <span className="text-gray-400">Belum diisi</span>}</p>
                  {detailModal.institution && <p className="text-sm text-gray-800 mt-1">Instansi: {detailModal.institution}</p>}
                </div>
                {detailModal.research_interests && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Minat Riset</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {detailModal.research_interests.split(',').map((ri, i) => (
                        <span key={i} className="bg-primary-50 text-primary-700 text-xs font-medium px-2.5 py-1 rounded-full">{ri.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(detailModal.linkedin || detailModal.google_scholar) && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Profil Online</h4>
                    {detailModal.linkedin && <a href={detailModal.linkedin} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline block">LinkedIn</a>}
                    {detailModal.google_scholar && <a href={detailModal.google_scholar} target="_blank" rel="noopener noreferrer" className="text-sm text-red-500 hover:underline block">Google Scholar</a>}
                  </div>
                )}
                {detailModal.message && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">Pesan</h4>
                    <p className="text-sm text-gray-800 italic">"{detailModal.message}"</p>
                  </div>
                )}
                <div className="text-xs text-gray-400">
                  Diajukan: {new Date(detailModal.created_at).toLocaleString('id-ID')}
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                {detailModal.status === 'pending' && (
                  <>
                    <button
                      onClick={() => { handleApprove(detailModal); setDetailModal(null); }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
                    >
                      <FaCheck /> Setujui
                    </button>
                    <button
                      onClick={() => { handleReject(detailModal.id); setDetailModal(null); }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-xl transition flex items-center justify-center gap-2"
                    >
                      <FaTimes /> Tolak
                    </button>
                  </>
                )}
                <button
                  onClick={() => setDetailModal(null)}
                  className="flex-1 border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium py-2.5 rounded-xl transition"
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequests;
